import { Chip } from "@/shared/components";
import { motion, useReducedMotion } from "motion/react";
import { Filter, FilterType } from "../types";

type FiltersProps = {
    filters: Filter[];
    setFilters: React.Dispatch<React.SetStateAction<Filter[]>>;
};

export function Filters({ filters, setFilters }: FiltersProps) {
    const handleChange = (clickedFilterType: FilterType, checked: boolean) => {
        // Filter logic:
        // 1. Clicking the "all" filter activates only "all" and deactivates all other filters. "All" cannot be deactivated directly.
        // 2. Clicking any other filter toggles its active state.
        // 3. Filter order should be:
        //    a. "All" filter first
        //    b. Active filters (newly activated filter should be added to the end of the active list)
        //    c. Inactive filters, sorted by their 'order' property
        // 4. Activating any filter (except "all") deactivates "all".
        // 5. If no filters (except "all") are active, "all" is automatically activated.

        setFilters((prevFilters) => {
            const allFilter = prevFilters.find((f) => f.type === FilterType.ALL)!;

            if (clickedFilterType === FilterType.ALL) {
                // Only "all" is active, others sorted by order
                return [
                    { ...allFilter, active: true },
                    ...prevFilters
                        .reduce<
                            Filter[]
                        >((acc, filter) => (filter.type !== FilterType.ALL ? [...acc, { ...filter, active: false }] : acc), [])
                        .sort((a, b) => a.order - b.order),
                ];
            }

            // Toggle the clicked filter
            const nextFilters = prevFilters.map((filter) => {
                if (filter.type === FilterType.ALL) return { ...filter, active: false };
                if (filter.type === clickedFilterType) return { ...filter, active: checked };
                return filter;
            });

            // If no filters (except "all") are active, activate "all"
            const anyActive = nextFilters.some((filter) => filter.type !== FilterType.ALL && filter.active);
            const finalFilters = nextFilters.map((filter) => (filter.type === FilterType.ALL ? { ...filter, active: !anyActive } : filter));

            // Order: all, active, inactive (by order)
            const active = finalFilters.filter((filter) => filter.type !== FilterType.ALL && filter.active);
            const inactive = finalFilters
                .filter((filter) => filter.type !== FilterType.ALL && !filter.active)
                .sort((a, b) => a.order - b.order);

            return [finalFilters.find((filter) => filter.type === FilterType.ALL)!, ...active, ...inactive];
        });
    };

    const prefersReducedMotion = useReducedMotion();
    const Component = prefersReducedMotion ? motion.li : "li";

    return (
        <ul className="flex flex-wrap gap-2">
            {filters.map((filter) => (
                <Component
                    key={filter.type}
                    layout="position"
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ type: "spring", stiffness: 400, damping: 35 }}
                >
                    <Chip label={filter.type} checked={filter.active} onChange={(checked) => handleChange(filter.type, checked)} />
                </Component>
            ))}
        </ul>
    );
}
